import { Outlet, ScrollRestoration, useRouteError, isRouteErrorResponse } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { PWAUpdatePrompt } from '@/components/PWAUpdatePrompt';
import { useSystemThemeListener, useVersionCheck } from '@/hooks';
import { forceReloadForUpdate } from '@/lib/force-reload';

export function Root() {
  const { t } = useTranslation('common');
  useSystemThemeListener();

  useVersionCheck({
    onUpdateAvailable: newVersion => {
      const v = newVersion?.trim();
      toast(t('appUpdate.available'), {
        description: v
          ? t('appUpdate.readyToUpdate', { version: v })
          : t('appUpdate.newAvailable'),
        duration: Infinity,
        action: {
          label: t('appUpdate.refresh'),
          onClick: () => forceReloadForUpdate(),
        },
      });
    },
    onCriticalUpdate: (reload, newVersion) => {
      const v = newVersion?.trim();
      toast.warning(
        v
          ? t('appUpdate.criticalRequiredWithVersion', { version: v })
          : t('appUpdate.criticalRequired'),
        {
          description: t('appUpdate.reloadToContinue'),
          duration: Infinity,
          action: {
            label: t('appUpdate.reload'),
            onClick: () => reload(),
          },
        }
      );
    },
  });

  return (
    <>
      <Outlet />
      <PWAUpdatePrompt />
      <Toaster position="bottom-right" />
      <ScrollRestoration />
    </>
  );
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('unable to preload css') ||
    message.includes("couldn't resolve module")
  );
}

export function RootErrorBoundary() {
  const { t } = useTranslation('common');
  const error = useRouteError();

  let message = t('errorPage.oops');
  let details = t('errorPage.unexpected');
  let stack: string | undefined;
  const isStaleCache = isChunkLoadError(error);

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : t('errorPage.error');
    details =
      error.status === 404 ? t('errorPage.notFoundMessage') : error.statusText || details;
  } else if (isStaleCache) {
    message = t('errorPage.updateRequiredTitle');
    details = t('errorPage.updateRequiredMessage');
  } else if (import.meta.env.DEV && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto flex min-h-screen flex-col items-center justify-center p-4">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold text-foreground">{message}</h1>
        <p className="mb-8 text-lg text-muted-foreground">{details}</p>
        {stack && (
          <pre className="mt-4 max-w-2xl overflow-x-auto rounded-lg bg-muted p-4 text-left text-sm">
            <code>{stack}</code>
          </pre>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          {isStaleCache ? (
            <button
              type="button"
              onClick={() => forceReloadForUpdate()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {t('errorPage.refreshNow')}
            </button>
          ) : (
            <>
              <a
                href="/"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {t('errorPage.goBackHome')}
              </a>
              <button
                type="button"
                onClick={() => forceReloadForUpdate()}
                className="inline-flex items-center gap-2 rounded-lg border border-input bg-background px-6 py-3 text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {t('errorPage.clearCacheReload')}
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default Root;
